/**
 * S-29 Data export sheet (V1 — GDPR #78). Serious surface.
 * Request queues an async bundle build (3/day cap); the download link travels
 * by email (mandatory `account` push/©account.export_ready) and redeeming it
 * here needs BOTH proofs: the emailed single-use token AND a fresh reauth —
 * token in the POST body, never a URL.
 */
import { useEffect, useState } from "react";
import { copy } from "@humble/shared";
import { Button, Sheet, toast } from "../../design/index.js";
import { API_BASE, api, isApiError } from "../../lib/api.js";
import { sessionStore } from "../../lib/store.js";

interface ExportRow {
  export_id?: string;
  state?: string;
  expires_at?: string;
}

export function ExportSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [rows, setRows] = useState<ExportRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [emailToken, setEmailToken] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    try {
      const list = await api.get("/me/export");
      setRows(Array.isArray(list) ? list : []);
    } catch {
      /* list is best-effort */
    }
  }

  useEffect(() => {
    if (open) void refresh();
  }, [open]);

  async function onRequest() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.post("/me/export");
      toast(copy.account.export_ready.replace("is ready", "was queued"), "info");
      await refresh();
    } catch (err) {
      setError(isApiError(err) ? err.message : "Network error — try again.");
    } finally {
      setBusy(false);
    }
  }

  async function onDownload(exportId: string) {
    if (busy) return;
    if (!emailToken || !password) {
      setError("Both proofs are required: the emailed token and your password.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const reauth = await api.post("/auth/reauth", { body: { password } });
      if (!reauth?.reauth_token) throw new Error("reauth failed");
      const token = sessionStore.get().accessToken;
      const res = await fetch(`${API_BASE}/me/export/${exportId}/download`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          ...(token ? { authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ token: emailToken.trim(), reauth_token: reauth.reauth_token }),
      });
      if (!res.ok) {
        const envelope = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(
          res.status === 410
            ? "That link expired or was already used. Request a new export."
            : (envelope.message ?? `Download failed (HTTP ${res.status}).`),
        );
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "humble-export.json";
      a.click();
      URL.revokeObjectURL(url);
      toast("Export downloaded.", "success");
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Download failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet open={open} onClose={onClose} title="Your data export">
      <p className="text-dim" style={{ fontSize: "0.92rem" }}>
        The bundle contains your own data: profile, verdicts you gave, matches, messages,
        purchases and your event log. Verdicts you received are included as counts only, and a
        Super Reject against you stays anonymous — other people's data is never exported.
      </p>
      <Button block variant="primary" loading={busy} onClick={() => void onRequest()}>
        Request a new export
      </Button>
      {rows.length > 0 && (
        <>
          <h3 style={{ fontSize: "0.95rem", marginBottom: 4 }}>Requested exports</h3>
          <ul style={{ paddingLeft: 18, margin: "4px 0 10px" }}>
            {rows.map((row) => (
              <li key={row.export_id} style={{ marginBottom: 8 }}>
                <span style={{ fontWeight: 600 }}>{row.state ?? "queued"}</span>
                {row.expires_at && (
                  <span className="text-faint">
                    {" "}
                    · link expires {new Date(row.expires_at).toLocaleString()}
                  </span>
                )}
                {row.state === "ready" && row.export_id && (
                  <Button
                    size="sm"
                    variant="ghost"
                    style={{ marginLeft: 8 }}
                    onClick={() => void onDownload(row.export_id!)}
                  >
                    Download
                  </Button>
                )}
              </li>
            ))}
          </ul>
          <p className="text-faint" style={{ fontSize: "0.85rem" }}>
            {copy.account.export_ready}
          </p>
          <div className="field">
            <label htmlFor="export-token">Emailed download token</label>
            <input
              id="export-token"
              type="text"
              autoComplete="off"
              value={emailToken}
              onChange={(e) => setEmailToken(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="export-password">Your password</label>
            <input
              id="export-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
        </>
      )}
      {error && (
        <p className="field__error" role="alert">
          {error}
        </p>
      )}
    </Sheet>
  );
}
